// Performance tracking and goals generation functionality via HTTP API
// 100% HTTP dependent - NO fallbacks, NO hardcoded data

import { get } from '@/api/lib/amplify-wrapper'
import { caseInsensitiveEquals } from './string-utils.service'
import { getAllMonumentaEmployees } from './employee.service'
import type { MonumentaEmployee } from './employee.service'
import { createImpersonatedApiHeaders, logApiCall } from './api-utils'

// ============================================================================
// 🚀 HTTP API CONFIGURATION
// ============================================================================

// HTTP API calls using AWS Amplify client - exactly like documents.ts
const performanceApiCall = async (endpoint: string, params: Record<string, string> = {}): Promise<any> => {
  const queryParams = new URLSearchParams(params)
  const path = `/monumenta/performance${endpoint ? `/${endpoint}` : ''}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
  
  logApiCall(path)
  
  const { response } = await get({
    apiName: 'monumenta',
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })
  
  const result = await response
  return await result.body.json()
}

// Employee API calls for some functions
const employeeApiCall = async (endpoint: string, params: Record<string, string> = {}): Promise<any> => {
  const queryParams = new URLSearchParams(params)
  const path = `/monumenta/employees${endpoint ? `/${endpoint}` : ''}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
  
  logApiCall(path)
  
  const { response } = await get({
    apiName: 'monumenta',
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })
  
  const result = await response
  return await result.body.json()
}

// ============================================================================
// 🚀 PERFORMANCE TYPES AND INTERFACES
// ============================================================================

interface Goal {
  id: string;
  name: string;
  value: number;
  percentage: number;
  color: string;
  description: string;
  isScale?: boolean;
  maxValue?: number;
}

// Director targets and goals interface
export interface DirectorTarget {
  directorFullName: string;
  department: string;
  nickname: string;
  client: string;
  meta2025: string;
  notes: string;
  // Parsed values
  targetValue: number;
  isPrimary: boolean;
}

// Director summary interface
export interface DirectorSummary {
  directorFullName: string;
  department: string;
  nickname: string;
  totalTarget: number;
  clients: Array<{
    client: string;
    target: number;
    isPrimary: boolean;
  }>;
}

// Performance distribution item
export interface PerformanceDistributionItem {
  category: string;
  value: number;
  color: string;
  icon: string;
  percentage: number;
  description: string;
}

// Overall targets summary
export interface OverallTargetsSummary {
  totalTargets: number;
  averageTargetPerDirector: number;
  uniqueDirectors: number;
  uniqueClients: number;
  uniqueDepartments: number;
  targetsByDepartment: Record<string, number>;
  targetsByClient: Record<string, number>;
}

// Performance statistics interface
export interface PerformanceStatistics {
  totalEmployees: number;
  highPerformers: {
    count: number;
    percentage: number;
  };
  lowPerformers: {
    count: number;
    percentage: number;
  };
  developmentNeeded: {
    count: number;
    percentage: number;
  };
  distribution: (PerformanceDistributionItem & { employees: MonumentaEmployee[] })[];
}

// Performance trends interface
export interface PerformanceTrendsResult {
  currentQuarter: {
    highPerformers: number;
    lowPerformers: number;
    averageScore: number;
  };
  previousQuarter: {
    highPerformers: number;
    lowPerformers: number;
    averageScore: number;
  };
  trend: 'improving' | 'declining' | 'stable';
  improvementAreas: string[];
}

// ============================================================================
// 🚀 PERFORMANCE DATA AND DISTRIBUTION
// ============================================================================

// Performance distribution from KPI analysis
export const PERFORMANCE_DISTRIBUTION = {
  'Estrela': { count: 16, percentage: 9.7, avgCost: 18100, description: 'Top Performers' },
  'Árvore': { count: 43, percentage: 26.1, avgCost: 6100, description: 'Core Team' },
  'Pessoas': { count: 38, percentage: 23.0, avgCost: 7100, description: 'People Focus' },
  'Blocos': { count: 26, percentage: 15.8, avgCost: 11500, description: 'Project Specialists' },
  'Experiência': { count: 8, percentage: 4.8, avgCost: 5700, description: 'New Hires' }
};

// Real Performance distribution based on PERFORMANCE_DISTRIBUTION data
export const REAL_PERFORMANCE_DISTRIBUTION = [
  { 
    category: 'Alta Performance', 
    value: 16, 
    color: '#000000', 
    icon: 'Star',
    percentage: 10,
    description: 'Funcionários de alta performance que excedem expectativas (Estrela)'
  },
  { 
    category: 'Potencial Crescimento', 
    value: 26, 
    color: '#3B82F6', 
    icon: 'Square',
    percentage: 16,
    description: 'Funcionários com potencial de crescimento identificado (Blocos)'
  },
  { 
    category: 'Desenvolvimento', 
    value: 38, 
    color: '#10B981', 
    icon: 'TrendingUp',
    percentage: 23,
    description: 'Funcionários em processo de desenvolvimento (Pessoas)'
  },
  { 
    category: 'Cumpre Função', 
    value: 43, 
    color: '#6B7280', 
    icon: 'TreePine',
    percentage: 26,
    description: 'Funcionários que cumprem adequadamente suas funções (Árvore)'
  },
  { 
    category: 'Sob Análise', 
    value: 8, 
    color: '#F59E0B', 
    icon: 'HelpCircle',
    percentage: 5,
    description: 'Funcionários em período de avaliação (Experiência)'
  },
  { 
    category: 'Desalinhado', 
    value: 0, 
    color: '#EF4444', 
    icon: 'Zap',
    percentage: 0,
    description: 'Funcionários que necessitam realinhamento (dados não disponíveis)'
  }
];

// Mapeamento de performance dos dados Excel para categorias do gráfico
export const PERFORMANCE_MAPPING = {
  'Estrela': 'Alta Performance',       // Preto - Alta performance e referência
  'Blocos': 'Potencial Crescimento',   // Azul - Potencial de crescimento
  'Pessoas': 'Desenvolvimento',        // Verde - Desenvolvimento
  'Árvore': 'Cumpre Função',          // Cinza - Cumpre função, mas estável
  'Interrogação': 'Sob Análise',      // Amarelo - Sob análise
  'Abacaxi': 'Desalinhado'            // Vermelho - Desalinhado e com alto custo
};

// Performance metrics for BI and PLANEJAMENTO teams (based on real performance distribution)
export const TECH_TEAM_PERFORMANCE = {
  totalEvaluationsCompleted: 95,
  averageScore360: 4.3,
  goalsAchieved: 87,
  teamSize: 7, // BI (4) + PLANEJAMENTO (3) team size
  performanceDistribution: {
    'Estrela': 1, // 14.3%
    'Árvore': 3, // 42.9%
    'Pessoas': 2, // 28.6%
    'Blocos': 1, // 14.3%
    'Experiência': 0 // 0%
  }
};

// ============================================================================
// 🎯 DIRECTOR TARGETS AND GOALS DATA (2025)
// ============================================================================

// Director targets data from CSV - Metas Diretores Mapeamento
export const DIRECTOR_TARGETS_2025: DirectorTarget[] = [
  {
    directorFullName: 'KASSYANA DE SOUZA SILVA',
    department: 'ATENDIMENTO',
    nickname: 'Kassy',
    client: 'BANCO DO BRASIL PROMO',
    meta2025: 'R$ 9.500.000,00',
    notes: 'Primary Director',
    targetValue: 9500000,
    isPrimary: true
  },
  {
    directorFullName: 'KASSYANA DE SOUZA SILVA',
    department: 'ATENDIMENTO',
    nickname: 'Kassy',
    client: 'BB SEGUROS + COLIGADAS',
    meta2025: 'R$ 23.700.000,00',
    notes: 'Primary Director',
    targetValue: 23700000,
    isPrimary: true
  },
  {
    directorFullName: 'VANESSA SANTANA RIBEIRO BESERRA',
    department: 'PRODUÇAO',
    nickname: 'Vanessa R',
    client: 'BANCO DO BRASIL PROMO',
    meta2025: 'R$ 9.500.000,00',
    notes: 'Secondary Director',
    targetValue: 9500000,
    isPrimary: false
  },
  {
    directorFullName: 'VANESSA SANTANA RIBEIRO BESERRA',
    department: 'PRODUÇAO',
    nickname: 'Vanessa R',
    client: 'BB SEGUROS + COLIGADAS',
    meta2025: 'R$ 23.700.000,00',
    notes: 'Secondary Director',
    targetValue: 23700000,
    isPrimary: false
  },
  {
    directorFullName: 'MARIANA GOMES DE LIMA GENOINO',
    department: 'ATENDIMENTO',
    nickname: 'Mari',
    client: 'BANCO DO BRASIL DIGITAL',
    meta2025: 'R$ 13.500.000,00',
    notes: 'Primary Director',
    targetValue: 13500000,
    isPrimary: true
  },
  {
    directorFullName: 'DIEGO DOMICIANO CASSIMIRO ISAAC',
    department: 'ESTRATEGIA E PLANEJAMENTO',
    nickname: 'Diego',
    client: 'BANCO DO BRASIL DIGITAL',
    meta2025: 'R$ 13.500.000,00',
    notes: 'Secondary Director',
    targetValue: 13500000,
    isPrimary: false
  },
  {
    directorFullName: 'ANDRE GUSTAVO FRATTI SILVA',
    department: 'PRODUÇAO',
    nickname: 'Fratti',
    client: 'SEBRAE',
    meta2025: 'R$ 16.000.000,00',
    notes: 'Primary Director',
    targetValue: 16000000,
    isPrimary: true
  },
  {
    directorFullName: 'ANDRE GUSTAVO FRATTI SILVA',
    department: 'PRODUÇAO',
    nickname: 'Fratti',
    client: 'EMBRATUR',
    meta2025: 'R$ 2.000.000,00',
    notes: 'Primary Director',
    targetValue: 2000000,
    isPrimary: true
  },
  {
    directorFullName: 'NATASHA GIOVANETTI DE ALBUQUERQUE PEDROSA',
    department: 'ATENDIMENTO',
    nickname: 'Nat',
    client: 'PETROBRAS',
    meta2025: 'R$ 50.000.000,00',
    notes: 'Primary Director',
    targetValue: 50000000,
    isPrimary: true
  },
  {
    directorFullName: 'NATASHA GIOVANETTI DE ALBUQUERQUE PEDROSA',
    department: 'ATENDIMENTO',
    nickname: 'Nat',
    client: 'FUNCEF',
    meta2025: 'R$ 950.000,00',
    notes: 'Primary Director',
    targetValue: 950000,
    isPrimary: true
  },
  {
    directorFullName: 'ANDRE GUSTAVO FRATTI SILVA',
    department: 'PRODUÇAO',
    nickname: 'Fratti',
    client: 'APEX',
    meta2025: 'R$ 2.000.000,00',
    notes: 'Primary Director',
    targetValue: 2000000,
    isPrimary: true
  }
];


// Get all unique directors
export const getAllDirectors = (): string[] => {
  return Array.from(new Set(DIRECTOR_TARGETS_2025.map(target => target.directorFullName)));
};

// Get director summary with all targets
export const getDirectorSummary = (directorFullName: string): DirectorSummary | null => {
  const directorTargets = DIRECTOR_TARGETS_2025.filter(target => 
    caseInsensitiveEquals(target.directorFullName, directorFullName)
  );
  
  if (directorTargets.length === 0) return null;
  
  const firstTarget = directorTargets[0];
  const totalTarget = directorTargets.reduce((sum, target) => sum + target.targetValue, 0);
  
  return {
    directorFullName: firstTarget.directorFullName,
    department: firstTarget.department,
    nickname: firstTarget.nickname,
    totalTarget,
    clients: directorTargets.map(target => ({
      client: target.client,
      target: target.targetValue,
      isPrimary: target.isPrimary
    }))
  };
};

// Get all director summaries
export const getAllDirectorSummaries = (): DirectorSummary[] => {
  const directors = getAllDirectors();
  return directors.map(director => getDirectorSummary(director)).filter(Boolean) as DirectorSummary[];
};

// Get targets by department
export const getTargetsByDepartment = (department: string): DirectorTarget[] => {
  return DIRECTOR_TARGETS_2025.filter(target => 
    caseInsensitiveEquals(target.department, department)
  );
};

// Get targets by client
export const getTargetsByClient = (client: string): DirectorTarget[] => {
  return DIRECTOR_TARGETS_2025.filter(target => 
    caseInsensitiveEquals(target.client, client)
  );
};

// Get total targets by department
export const getTotalTargetsByDepartment = (): Record<string, number> => {
  const departments = Array.from(new Set(DIRECTOR_TARGETS_2025.map(target => target.department)));
  return departments.reduce((acc, dept) => {
    const deptTargets = getTargetsByDepartment(dept);
    acc[dept] = deptTargets.reduce((sum, target) => sum + target.targetValue, 0);
    return acc;
  }, {} as Record<string, number>);
};

// Get total targets by client
export const getTotalTargetsByClient = (): Record<string, number> => {
  const clients = Array.from(new Set(DIRECTOR_TARGETS_2025.map(target => target.client)));
  return clients.reduce((acc, client) => {
    const clientTargets = getTargetsByClient(client);
    acc[client] = clientTargets.reduce((sum, target) => sum + target.targetValue, 0);
    return acc;
  }, {} as Record<string, number>);
};

// Get overall targets summary
export const getOverallTargetsSummary = (): OverallTargetsSummary => {
  const totalTargets = DIRECTOR_TARGETS_2025.reduce((sum, target) => sum + target.targetValue, 0);
  const uniqueDirectors = getAllDirectors().length;
  const uniqueClients = Array.from(new Set(DIRECTOR_TARGETS_2025.map(target => target.client))).length;
  const uniqueDepartments = Array.from(new Set(DIRECTOR_TARGETS_2025.map(target => target.department))).length;
  
  return {
    totalTargets,
    averageTargetPerDirector: totalTargets / uniqueDirectors,
    uniqueDirectors,
    uniqueClients,
    uniqueDepartments,
    targetsByDepartment: getTotalTargetsByDepartment(),
    targetsByClient: getTotalTargetsByClient()
  };
};

// ============================================================================
// 🚀 PERFORMANCE QUERY FUNCTIONS
// ============================================================================

// Helper functions for performance data
export const getTotalPerformanceCount = (): number => {
  return REAL_PERFORMANCE_DISTRIBUTION.reduce((sum, item) => sum + item.value, 0);
};

export const getPerformanceByCategory = (category: string) => {
  return REAL_PERFORMANCE_DISTRIBUTION.find(item => item.category === category);
};

export const getHighPerformers = (): PerformanceDistributionItem[] => {
  return REAL_PERFORMANCE_DISTRIBUTION.filter(item => 
    item.category === 'Alta Performance' || item.category === 'Potencial Crescimento'
  );
};

export const getLowPerformers = (): PerformanceDistributionItem[] => {
  return REAL_PERFORMANCE_DISTRIBUTION.filter(item => 
    item.category === 'Sob Análise' || item.category === 'Desalinhado'
  );
};

// Função para mapear performance do funcionário para categoria do gráfico
export const mapEmployeePerformanceToCategory = (performance: string): string => {
  return PERFORMANCE_MAPPING[performance as keyof typeof PERFORMANCE_MAPPING] || 'Cumpre Função';
};

// Função para obter funcionários por categoria de performance
export const getEmployeesByPerformanceCategory = async (category: string): Promise<MonumentaEmployee[]> => {
  const employees = await getAllMonumentaEmployees();
  return employees.filter(employee =>
    caseInsensitiveEquals(mapEmployeePerformanceToCategory(employee.performance), category)
  );
};

// Função para calcular distribuição real de performance baseada nos dados dos funcionários
export const calculateRealPerformanceDistribution = async (): Promise<(PerformanceDistributionItem & { employees: MonumentaEmployee[] })[]> => {
  const allEmployees = await getAllMonumentaEmployees();

  const distribution = await Promise.all(REAL_PERFORMANCE_DISTRIBUTION.map(async (category) => {
    const employees = await getEmployeesByPerformanceCategory(category.category);
    return {
      ...category,
      value: employees.length,
      percentage: Math.round((employees.length / allEmployees.length) * 100),
      employees: employees
    };
  }));

  return distribution;
};

// Get performance statistics summary
export const getPerformanceStatistics = async (): Promise<PerformanceStatistics> => {
  const employees = await getAllMonumentaEmployees();
  const totalEmployees = employees.length;
  const highPerformers = employees.filter(emp =>
    emp.performance === 'Alta Performance' || emp.performance === 'Potencial Crescimento'
  );
  const lowPerformers = employees.filter(emp =>
    emp.performance === 'Sob Análise' || emp.performance === 'Desalinhado'
  );
  const developmentNeeded = employees.filter(emp =>
    emp.performance === 'Desenvolvimento' || emp.performance === 'Cumpre Função'
  );

  return {
    totalEmployees,
    highPerformers: {
      count: highPerformers.length,
      percentage: Math.round((highPerformers.length / totalEmployees) * 100)
    },
    lowPerformers: {
      count: lowPerformers.length,
      percentage: Math.round((lowPerformers.length / totalEmployees) * 100)
    },
    developmentNeeded: {
      count: developmentNeeded.length,
      percentage: Math.round((developmentNeeded.length / totalEmployees) * 100)
    },
    distribution: await calculateRealPerformanceDistribution()
  };
};

// Get performance trends (simulated for demo)
export const getPerformanceTrends = (): PerformanceTrendsResult => {
  return {
    currentQuarter: {
      highPerformers: 55,
      lowPerformers: 10,
      averageScore: 82.5
    },
    previousQuarter: {
      highPerformers: 52,
      lowPerformers: 13,
      averageScore: 78.3
    },
    trend: 'improving' as 'improving' | 'declining' | 'stable',
    improvementAreas: [
      'Communication skills',
      'Technical expertise',
      'Project management'
    ]
  };
};

// ============================================================================
// 🎯 PERFORMANCE DATA ACCESS VIA HTTP API
// ============================================================================

/**
 * Get team performance data for a manager - 100% HTTP, NO fallbacks
 */
export const getTeamPerformanceData = async (userEmail: string) => {
  
  const response = await performanceApiCall(`${encodeURIComponent(userEmail)}/team-data`)
  
  if (!response.teamMembers) {
    throw new Error('Invalid API response: missing teamMembers array')
  }
  
  return response.teamMembers
}

/**
 * Get team performance statistics - 100% HTTP, NO fallbacks
 */
export const getTeamPerformanceStats = async (userEmail: string) => {
  
  const response = await performanceApiCall(`${encodeURIComponent(userEmail)}/team-stats`)
  
  if (!response.stats) {
    throw new Error('Invalid API response: missing stats object')
  }
  
  return response.stats
}

/**
 * Get performance alerts for a team - 100% HTTP, NO fallbacks
 */
export const getPerformanceAlerts = async (userEmail: string) => {
  
  const response = await performanceApiCall(`${encodeURIComponent(userEmail)}/alerts`)
  
  if (!response.alerts) {
    throw new Error('Invalid API response: missing alerts array')
  }
  
  return response.alerts
}

/**
 * Get performance categories for an employee - 100% HTTP, NO fallbacks
 */
export const getPerformanceCategories = async (userEmail: string) => {
  
  const response = await performanceApiCall(`${encodeURIComponent(userEmail)}/categories`)
  
  if (!response.categories) {
    throw new Error('Invalid API response: missing categories array')
  }
  
  return response.categories
}

/**
 * Get department performance overview - 100% HTTP, NO fallbacks
 */
export const getDepartmentPerformanceOverview = async (department: string) => {
  
  const response = await performanceApiCall(`departments/${encodeURIComponent(department)}`)
  
  if (!response.department) {
    throw new Error('Invalid API response: missing department data')
  }
  
  return response
}

// ============================================================================
// 🎯 GOALS GENERATION SYSTEM
// ============================================================================

// Helper function to get current user subordinates - 100% HTTP, NO fallbacks
const getCurrentUserSubordinates = async (userEmail?: string): Promise<any[]> => {
  if (!userEmail) {
    throw new Error('User email is required')
  }
  
  
  const response = await employeeApiCall(`by-email/${encodeURIComponent(userEmail)}/subordinates`)
  
  if (!response.subordinates) {
    throw new Error('Invalid API response: missing subordinates array')
  }
  
  return response.subordinates
}

// Helper function to get team summary - 100% HTTP, NO fallbacks
const getCurrentUserTeamSummary = async (userEmail?: string) => {
  if (!userEmail) {
    throw new Error('User email is required')
  }
  
  
  const response = await employeeApiCall(`by-email/${encodeURIComponent(userEmail)}/team-summary`)
  
  if (!response.teamSummary) {
    throw new Error('Invalid API response: missing teamSummary object')
  }
  
  return response.teamSummary
}

// Dynamic goals generation based on user role and performance - HTTP API version
export const generateUserGoals = async (user: any): Promise<Goal[]> => {
  const userRole = user.role.toUpperCase();
  const subordinates = await getCurrentUserSubordinates(user.email);
  const isLeadership = ['DIRETOR', 'GERENTE', 'COORDENADOR', 'LIDER'].some(keyword => userRole.includes(keyword));
  
  if (isLeadership && subordinates.length > 0) {
    // Leadership goals based on team performance
    const teamSummary = await getCurrentUserTeamSummary(user.email);
    // Safe access with proper typing
    const metrics = teamSummary?.performanceMetrics || { highPerformers: 0, potentialGrowth: 0, needsDevelopment: 0 };
    const highPerformerPercentage = Math.round((metrics.highPerformers / subordinates.length) * 100);
    const avgVacationBalance = Math.round(teamSummary?.averageVacationBalance || 0);
    
    return [
      {
        id: 'team-performance',
        name: 'Performance da Equipe',
        value: highPerformerPercentage,
        percentage: highPerformerPercentage,
        color: '#3B82F6',
        description: `${metrics.highPerformers} de ${subordinates.length} com alta performance`
      },
      {
        id: 'team-development',
        name: 'Desenvolvimento',
        value: Math.round(((subordinates.length - metrics.needsDevelopment) / subordinates.length) * 100),
        percentage: Math.round(((subordinates.length - metrics.needsDevelopment) / subordinates.length) * 100),
        color: '#10B981',
        description: 'Equipe em desenvolvimento contínuo'
      },
      {
        id: 'vacation-management',
        name: 'Gestão de Férias',
        value: Math.max(0, 100 - Math.round((avgVacationBalance / 30) * 100)),
        percentage: Math.max(0, 100 - Math.round((avgVacationBalance / 30) * 100)),
        color: '#F59E0B',
        description: `Média de ${avgVacationBalance} dias por pessoa`
      },
      {
        id: 'team-growth',
        name: 'Crescimento da Equipe',
        value: subordinates.length,
        percentage: Math.min(100, (subordinates.length / 10) * 100),
        color: '#8B5CF6',
        description: `${subordinates.length} pessoas na equipe`
      }
    ];
  } else {
    // Individual contributor goals
    const performanceScore = user.performance === 'ALTA PERFORMANCE' ? 90 :
                            user.performance === 'POTENCIAL CRESCIMENTO' ? 75 :
                            user.performance === 'CUMPRE FUNÇÃO' ? 60 : 50;
    
    return [
      {
        id: 'personal-performance',
        name: 'Performance Pessoal',
        value: performanceScore,
        percentage: performanceScore,
        color: '#3B82F6',
        description: 'Avaliação de desempenho atual'
      },
      {
        id: 'skill-development',
        name: 'Desenvolvimento',
        value: 85,
        percentage: 85,
        color: '#10B981',
        description: 'Cursos e treinamentos'
      },
      {
        id: 'goals-achieved',
        name: 'Metas Atingidas',
        value: 7,
        percentage: 70,
        color: '#F59E0B',
        description: '7 de 10 metas cumpridas'
      },
      {
        id: 'vacation-balance',
        name: 'Saldo de Férias',
        value: Math.max(0, 100 - Math.round((user.vacationBalance / 30) * 100)),
        percentage: Math.max(0, 100 - Math.round((user.vacationBalance / 30) * 100)),
        color: '#8B5CF6',
        description: `${user.vacationBalance} dias disponíveis`
      }
    ];
  }
};

// Get team performance analysis - HTTP API version
export const getTeamPerformanceAnalysis = async (userEmail?: string) => {
  try {
    if (!userEmail) {
      return {
        hasTeam: false,
        message: 'Email do usuário é obrigatório'
      };
    }
    
    
    const [subordinates, teamSummary] = await Promise.all([
      getCurrentUserSubordinates(userEmail),
      getCurrentUserTeamSummary(userEmail)
    ]);
  
  if (subordinates.length === 0) {
    return {
      hasTeam: false,
      message: 'Nenhuma equipe encontrada'
    };
  }
  
  return {
    hasTeam: true,
    teamSize: subordinates.length,
      performanceMetrics: teamSummary?.performanceMetrics,
      performanceDistribution: teamSummary?.performanceDistribution,
    recommendations: generateTeamRecommendations(subordinates)
  };
  } catch (error) {
    console.error('Failed to get team performance analysis:', error);
    return {
      hasTeam: false,
      message: 'Erro ao carregar análise de performance da equipe'
    };
  }
};

// Generate recommendations for team improvement
const generateTeamRecommendations = (team: any[]): string[] => {
  const recommendations: string[] = [];
  
  const lowPerformers = team.filter(emp => 
    emp.performance === 'Experiência' || emp.alertStatus === 'urgent'
  );
  
  const highVacationBalance = team.filter(emp => emp.vacationBalance > 30);
  
  if (lowPerformers.length > 0) {
    recommendations.push(`${lowPerformers.length} funcionário(s) precisam de atenção especial`);
  }
  
  if (highVacationBalance.length > 0) {
    recommendations.push(`${highVacationBalance.length} funcionário(s) têm saldo alto de férias`);
  }
  
  const needsAttention = team.filter(emp => emp.alertStatus === 'attention');
  if (needsAttention.length > 0) {
    recommendations.push(`${needsAttention.length} funcionário(s) precisam de acompanhamento`);
  }
  
  if (recommendations.length === 0) {
    recommendations.push('Equipe está bem alinhada, continue o bom trabalho!');
  }
  
  return recommendations;
};